/**
 * Settings → Spending — one route, two audiences.
 *
 * The org dashboard is gated on `manage_budget:read`, and until now a member
 * without it got only refusal copy: "You do not have permission to view
 * organization budgets." True, and useless — the member DOES have spend, they
 * just have their own rather than everyone's. The route answered a question
 * nobody asked instead of the one they came with.
 *
 * So the capability now picks the VIEW rather than deciding pass/fail:
 *
 *   manage_budget:read  →  OrgBudgetDashboard  (everyone's spend, the org's caps)
 *   otherwise           →  MemberUsageView     (their own spend, self-scoped)
 *
 * That is not a widening of access. `MemberUsageView` reads
 * `GET /orgs/:id/usage/me`, which is membership-only and scoped from the auth
 * context with **no email parameter** — a member cannot ask for anyone else's
 * figures by crafting the request, and no capability was bypassed to render it.
 * The org dashboard's own `canRead` self-gate is untouched and still refuses in
 * words if it is ever mounted directly.
 *
 * Personal drives get a THIRD arrangement — `OrgBudgetDashboard` in `personal`
 * mode, not `MemberUsageView`. (An earlier draft of this note said otherwise;
 * the reasoning for the change is at the branch itself, below.) The old comment
 * in SettingsView said Spending "deliberately does NOT" render on a personal
 * drive, because there are no roles, teams or members to allocate a budget
 * across — that reasoning was about the per-member and per-team TABLES, and it
 * still holds. `personal` mode drops exactly those.
 *
 * ── There is no longer an Account → Usage item ──
 * It rendered this same component and was justified as deliberate duplication
 * (2026-08-07): "a member should find their spend under the name their admin
 * uses for it, without losing the account-level entry point they may already
 * know." In practice a member got TWO sidebar entries for one subject, each
 * showing a strict subset of the other — the `variant` prop existed purely to
 * stop them being byte-identical, which is a fix for a symptom.
 *
 * Merged, not deleted: this page now shows the union (the attribution
 * waterfall AND spend by bucket AND spend by feature), so nothing that lived
 * only under Usage was lost. **`/settings/usage` still resolves here** — it is
 * bookmarked, it is what `settings.billing.noAccessBody` names in four
 * locales, and it is the else-branch of `onOpenBudgets` on both shells.
 */
import { useTranslation } from '@papan/i18n'
import { useMyCapabilities } from '@papan/core-ui/ui'
import { OrgBudgetDashboard } from './OrgBudgetDashboard'
import { MemberUsageView } from './MemberUsageView'

export function SpendingPage({
  tenantId,
  userEmail,
  personal,
  onNavigate,
  onOpenBilling,
}: {
  tenantId?: string
  userEmail?: string
  /** Personal drive — no roles or teams to attribute against. */
  personal?: boolean
  /** Jump to the Budget editor; only meaningful for a caller who may write. */
  onNavigate?: () => void
  /** Jump to Settings → Billing, for the prepaid-credit card's link. */
  onOpenBilling?: () => void
}) {
  // `personal` (not a hardcoded false) so a personal drive issues NO
  // capabilities request at all — the short-circuit below never consults
  // `can()`, so the answer was fetched and discarded.
  const { t } = useTranslation('core-ui')
  const { can, loading } = useMyCapabilities(tenantId, personal, userEmail)

  // A personal drive gets the SAME dashboard in `personal` mode, not a copy of
  // the Usage tab. Rendering `MemberUsageView` here (the first cut) made
  // Spending byte-identical to the Usage item two rows above it in the same
  // sidebar — same component, same props, same "Usage" heading — which is
  // duplication, not a second page.
  //
  // The dashboard is the right shape because it carries what Usage does not:
  // **Spend by feature**. Usage answers "which pool is funding me"; Spending
  // answers "what did I spend it on". `personal` mode drops the per-member and
  // per-team tables, their charts and the member-count tile — all sets of one
  // or zero on a personal drive — and relabels the tiles to the personal
  // wording. That keeps the two tabs genuinely distinct.
  if (personal) {
    return <OrgBudgetDashboard tenantId={tenantId} userEmail={userEmail} onOpenBilling={onOpenBilling} personal />
  }

  // `can()` answers false while the capabilities request is in flight, so
  // rendering on it would show EVERY admin the member view first, fire a
  // wasted `/usage/me`, then swap — a visible content flip on the primary
  // admin flow every hard load. Wait for the answer instead.
  if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>

  if (can('manage_budget', 'read')) {
    return <OrgBudgetDashboard tenantId={tenantId} userEmail={userEmail} onNavigate={onNavigate} onOpenBilling={onOpenBilling} />
  }

  // An org member cannot call `GET /:id/usage` at all, so they cannot have the
  // dashboard — but they can have the QUESTION it answers, self-scoped.
  //
  // This used to pass `variant="spending"`, which existed only to stop this
  // page and Settings → Usage rendering identically for the same reader. There
  // is no Settings → Usage any more (§4.1): the two nav items were one subject
  // filed twice, and the fix was to MERGE them rather than delete either — so
  // this now shows the union, which is what each half was missing.
  return <MemberUsageView tenantId={tenantId} />
}
